import api from './api';
import type { StudentProgram } from '../types';
import type {
  AdditionalPayment,
  ApplicationIntent,
  ContractDetails,
  QuoteResponse,
  StudentWorkspace,
  PaymentTransaction,
  PricingCatalogue,
  SignupCountry,
  StudentContract,
  StudentPortalProfile,
  StudentPortalProfileUpdate,
  UniversityApplicationItem,
} from '../types/studentPortal';

class StudentPortalService {
  async getSignupCountries(): Promise<SignupCountry[]> {
    const { data } = await api.get<SignupCountry[]>('/student-portal/countries');
    return data;
  }
  async getPricingCatalogue(countryId: number): Promise<PricingCatalogue> {
    const { data } = await api.get<PricingCatalogue>(`/student-portal/countries/${countryId}/pricing`);
    return data;
  }
  async getWorkspace(): Promise<StudentWorkspace> {
    const { data } = await api.get<StudentWorkspace>('/student-portal/workspace');
    return data;
  }

  async getProfile(): Promise<StudentPortalProfile> {
    const { data } = await api.get<StudentPortalProfile>('/student-portal/profile');
    return data;
  }

  async updateProfile(payload: StudentPortalProfileUpdate): Promise<StudentPortalProfile> {
    const { data } = await api.patch<StudentPortalProfile>('/student-portal/profile', payload);
    return data;
  }

  async getOnboarding(): Promise<ApplicationIntent | null> {
    const { data } = await api.get<ApplicationIntent | null>('/student-portal/onboarding');
    return data;
  }

  async selectCountry(countryId: number): Promise<ApplicationIntent> {
    const { data } = await api.put<ApplicationIntent>('/student-portal/onboarding/country', { country_id: countryId });
    return data;
  }

  async saveAnswers(answers: Record<string, unknown>): Promise<ApplicationIntent> {
    const { data } = await api.put<ApplicationIntent>('/student-portal/onboarding/answers', { answers });
    return data;
  }

  async getPrograms(): Promise<StudentProgram[]> {
    const { data } = await api.get<StudentProgram[]>('/admissions/programs');
    return data;
  }

  async generateQuote(programId: number): Promise<QuoteResponse> {
    const { data } = await api.post<QuoteResponse>('/student-portal/onboarding/quote', { program_id: programId });
    return data;
  }

  async acceptQuote(): Promise<{ application_id: number; application_number: string; next_route: string }> {
    const { data } = await api.post('/student-portal/onboarding/quote/accept');
    return data;
  }

  async getContract(): Promise<StudentContract> {
    const { data } = await api.get<StudentContract>('/student-portal/contract');
    return data;
  }

  async saveContractDetails(details: ContractDetails): Promise<StudentContract> {
    // The deployed API still requires this retired field. Keep it compatible
    // without exposing an irrelevant question to the student.
    const payload: ContractDetails = {
      ...details,
      desired_specialization: details.desired_specialization?.trim() || 'غير محدد',
      passport_number: details.passport_number?.trim() || null,
      guardian_name: details.guardian_name?.trim() || null,
      guardian_national_id: details.guardian_national_id?.trim() || null,
      guardian_phone: details.guardian_phone?.trim() || null,
      file_notes: details.file_notes?.trim() || null,
    };
    const { data } = await api.put<StudentContract>('/student-portal/onboarding/contract-details', payload);
    return data;
  }

  async reviewContract(): Promise<StudentContract> {
    const { data } = await api.post<StudentContract>('/student-portal/onboarding/contract/review');
    return data;
  }

  async getUniversities(): Promise<UniversityApplicationItem[]> {
    const { data } = await api.get<UniversityApplicationItem[]>('/student-portal/universities');
    return data;
  }

  async decideUniversity(id: number, decision: 'approve' | 'decline', note?: string): Promise<UniversityApplicationItem> {
    const { data } = await api.post<UniversityApplicationItem>(`/student-portal/universities/${id}/decision`, { decision, note });
    return data;
  }

  async getAdditionalPayments(): Promise<AdditionalPayment[]> {
    const { data } = await api.get<AdditionalPayment[]>('/student-portal/additional-payments');
    return data;
  }

  async submitAdditionalPayment(
    id: number,
    payload: {
      method: string;
      /** Required by the API when method is "other". */
      method_other?: string;
      reference: string;
      proof_file_url: string;
      /** Every proof file, first one included. */
      proof_file_urls?: string[];
      idempotency_key: string;
    },
  ): Promise<PaymentTransaction> {
    const { data } = await api.post<PaymentTransaction>(`/student-portal/additional-payments/${id}/submit`, payload);
    return data;
  }
}

export default new StudentPortalService();
